import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import { ARTIST_TECH_SECTIONS } from "../../constants";

const ArtistTechHospitalityModal = ({
  isOpen,
  onClose,
  section,
  artist,
  onSubmit,
  loading,
}) => {
  const config = ARTIST_TECH_SECTIONS[section];
  const [form, setForm] = useState({});
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && config) {
      const initial = {};
      config.fields.forEach((f) => {
        initial[f.key] = artist?.[f.key] || "";
      });
      setForm(initial);
      setPdf(null);
      setError("");
    }
  }, [isOpen, section, artist]);

  if (!isOpen || !config) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleSubmit = () => {
    const payload = {
      ...form,
    };

    if (config.documentKey && pdf) {
      payload[config.documentKey] = pdf;
    }

    onSubmit(payload, section, setError);
  };

  const StarRating = ({ value, max = 5, onChange }) => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: max }).map((_, i) => {
          const starValue = i + 1;
          const active = starValue <= value;

          return (
            <button
              key={starValue}
              type="button"
              onClick={() => onChange(starValue)}
              className={`text-xl transition ${
                active
                  ? "text-yellow-400"
                  : "text-slate-500 hover:text-yellow-300"
              }`}
            >
              ★
            </button>
          );
        })}

        {value > 0 && (
          <span className="ml-2 text-sm text-slate-300">
            {value} / {max}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">{config.title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {config.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm text-slate-300 mb-1">
                {field.label}
              </label>

              {field.type === "rating" ? (
                <StarRating
                  value={Number(form[field.key]) || 0}
                  max={field.max || 5}
                  onChange={(val) => handleChange(field.key, val)}
                />
              ) : field.type === "boolean" ? (
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={!!form[field.key]}
                    onChange={(e) => handleChange(field.key, e.target.checked)}
                    className="w-5 h-5 accent-pink-600"
                  />
                  <span className="text-slate-200">
                    {form[field.key] ? "Yes" : "No"}
                  </span>
                </label>
              ) : (
                <input
                  type="text"
                  value={form[field.key]}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white
          focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              )}
            </div>
          ))}

          {config.documentKey && (
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Full Document (PDF)
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setPdf(e.target.files[0])}
                className="text-sm text-slate-400"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtistTechHospitalityModal;
